// Просмотр элемента целиком: галерея всех его картинок + поля списком.
// Read-only и без диалога вокруг — тот же блок вставляют самостоятельное окно
// (`ElementDetailsDialog`), панель внутри пикера и карточка из истории сообщений.
// На входе готовый «вид», поэтому у истории полей меньше, чем у элемента
// библиотеки, без развилок в самой разметке.

import { useEffect, useState } from "react";
import type { TFunction } from "i18next";
import { Images } from "lucide-react";

import { cn } from "~/shared/lib/utils";
import { MediaThumb } from "~/shared/ui/media-thumb";

import type { ElementDetailsView } from "../lib/element-detail-fields";

interface ElementDetailsProps {
    t: TFunction;
    view: ElementDetailsView;
    /** Клик по крупной картинке — открыть лайтбокс с её индексом. Не задан → картинка некликабельна. */
    onOpenImage?: (index: number) => void;
}

export function ElementDetails({ t, view, onOpenImage }: ElementDetailsProps) {
    const [activeIndex, setActiveIndex] = useState(0);

    // Блок переиспользуется между элементами (пикер, история): без сброса на
    // элементе с меньшим числом картинок крупное превью осталось бы пустым —
    // индекс от предыдущего элемента.
    useEffect(() => setActiveIndex(0), [view]);

    const active = view.images[activeIndex] ?? view.images[0] ?? null;
    const activeSlide = view.images[activeIndex] !== undefined ? activeIndex : 0;

    return (
        <div className="flex flex-col gap-4 md:flex-row md:gap-5">
            <div className="flex w-full shrink-0 flex-col gap-2 md:w-64">
                <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-zinc-900">
                    {active ? (
                        onOpenImage ? (
                            <button
                                type="button"
                                onClick={() => onOpenImage(activeSlide)}
                                aria-label={t("elements:details.viewImage", { index: activeSlide + 1 })}
                                className="absolute inset-0"
                            >
                                <MediaThumb url={active} kind="image" className="absolute inset-0" />
                            </button>
                        ) : (
                            <MediaThumb url={active} kind="image" className="absolute inset-0" />
                        )
                    ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                            <Images className="size-8" aria-hidden />
                        </div>
                    )}
                </div>

                {view.images.length > 1 && (
                    <div className="flex flex-wrap gap-2">
                        {view.images.map((url, index) => (
                            <button
                                key={url}
                                type="button"
                                aria-pressed={index === activeSlide}
                                aria-label={t("elements:details.showImage", { index: index + 1 })}
                                onClick={() => setActiveIndex(index)}
                                className={cn(
                                    "relative size-12 overflow-hidden rounded border transition-colors",
                                    index === activeSlide
                                        ? "border-violet-400/70"
                                        : "border-border hover:border-white/25",
                                )}
                            >
                                <MediaThumb url={url} kind="image" className="absolute inset-0" />
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="flex min-w-0 flex-1 flex-col gap-3">
                <h3 className="text-base leading-tight font-medium break-words">{view.name}</h3>

                <dl className="flex flex-col gap-3">
                    {view.fields.map((field) => (
                        <div
                            key={field.key}
                            className={cn(
                                "gap-3",
                                field.multiline
                                    ? "flex flex-col gap-1"
                                    : "flex items-baseline justify-between",
                            )}
                        >
                            <dt className="shrink-0 text-xs text-muted-foreground">{field.label}</dt>
                            <dd
                                className={cn(
                                    "min-w-0 text-sm",
                                    field.multiline ? "whitespace-pre-wrap" : "text-right",
                                )}
                            >
                                {field.value}
                            </dd>
                        </div>
                    ))}
                </dl>
            </div>
        </div>
    );
}
